import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { useAuth } from "./auth";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const formatDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const toISO = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const AuditRepairPage = () => {
  const today = useMemo(() => new Date(), []);
  const { canEdit } = useAuth();
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);
  const [from, setFrom] = useState(formatDateInput(defaultFrom));
  const [to, setTo] = useState(formatDateInput(today));
  const [summary, setSummary] = useState(null);
  const [missingLogs, setMissingLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState("");

  const loadAudit = async () => {
    if (!from || !to) return;
    setLoading(true);
    setRepairResult("");
    try {
      const [summaryRes, missingRes] = await Promise.all([
        fetch(`${API_BASE}/api/audit/summary?from=${from}&to=${to}`),
        fetch(`${API_BASE}/api/audit/missing-visit-logs?from=${from}&to=${to}`),
      ]);
      const summaryData = summaryRes.ok ? await summaryRes.json() : null;
      const missingData = missingRes.ok ? await missingRes.json() : [];
      setSummary(summaryData);
      setMissingLogs(Array.isArray(missingData) ? missingData : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudit();
  }, [from, to]);

  const handleRepair = async () => {
    if (!from || !to) return;
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    setRepairing(true);
    setRepairResult("");
    try {
      const res = await fetch(`${API_BASE}/api/audit/repair/visit-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from: toISO(from), to: toISO(to), maxInsert: 1000 }),
      });
      if (!res.ok) {
        setRepairResult("Repair failed. Check server logs.");
        return;
      }
      const data = await res.json();
      setRepairResult(`Inserted ${data.inserted || 0} missing visit logs.`);
      await loadAudit();
    } catch (error) {
      console.error(error);
      setRepairResult("Repair failed. Check server logs.");
    } finally {
      setRepairing(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Card elevation={1}>
          <CardHeader title="Audit & Repair" />
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField
                label="From"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={loadAudit} disabled={loading}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                onClick={handleRepair}
                disabled={repairing || !missingLogs.length || !canEdit}
              >
                Repair Missing Logs
              </Button>
              {repairResult && (
                <Typography variant="body2" color="text.secondary">
                  {repairResult}
                </Typography>
              )}
            </Stack>
            {loading && <LinearProgress sx={{ mt: 2 }} />}
          </CardContent>
        </Card>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Missing Visit Logs
              </Typography>
              <Typography variant="h4">
                {summary?.missingVisitLogs ?? "—"}
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Missing Complainant Name
              </Typography>
              <Typography variant="h4">
                {summary?.missingComplainant ?? "—"}
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Summons Without Enquiry No
              </Typography>
              <Typography variant="h4">
                {summary?.missingEnquiryOnSummons ?? "—"}
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Appearance Before Dispatch
              </Typography>
              <Typography variant="h4">
                {summary?.appearanceBeforeDispatch ?? "—"}
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Card elevation={1}>
          <CardHeader
            title="Missing Visit Logs"
            subheader="Appearance dates without a matching visit log"
          />
          <Divider />
          <CardContent>
            {missingLogs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No missing logs found in this range.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Enquiry No</TableCell>
                      <TableCell>Person</TableCell>
                      <TableCell>Appearance Date</TableCell>
                      <TableCell>Notice</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingLogs.map((row, idx) => (
                      <TableRow key={`${row.enquirycasenum}-${row.cname}-${idx}`}>
                        <TableCell>{row.enquirycasenum || "—"}</TableCell>
                        <TableCell>{row.cname || "—"}</TableCell>
                        <TableCell>{row.appearance_date || "—"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.label || "Notice"} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default AuditRepairPage;
